import { extractErrorMessage } from '@/lib/api-error';
import { buildRequestHeaders } from '@/lib/auth/build-request-headers';
import { getConfig } from '@/lib/config/config-registry';

/**
 * Performs an authenticated request against the backend (BFF) and returns the
 * raw `Response` after asserting it is OK. Resolves the base URL and auth from
 * the host `TemplateBuilderConfig` (via the registry), so the published library
 * uses the consumer's URLs + token rather than our build-time env.
 *
 * On a 401 it refreshes the token once (`auth.refreshAccessToken`) and retries
 * the request a single time with freshly built headers — no loop. A non-401
 * non-OK response throws an `Error` carrying the backend's error message, or
 * `${errorContext}: ${status}` when the body has none.
 *
 * `path` may already include a query string. Prefer {@link backendRequest} /
 * {@link backendRequestVoid} over calling this directly.
 */
async function backendFetch(
  path: string,
  errorContext: string,
  init?: RequestInit,
): Promise<Response> {
  const config = getConfig();
  const url = `${config.backendApiUrl}${path}`;

  const send = async (): Promise<Response> =>
    fetch(url, {
      method: 'GET',
      ...init,
      headers: { ...(await buildRequestHeaders()), ...init?.headers },
    });

  let response = await send();

  if (response.status === 401) {
    await config.auth.refreshAccessToken();
    response = await send();
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `${errorContext}: ${response.status}`));
  }

  return response;
}

/**
 * Authenticated backend request that parses and returns the JSON body as `T`.
 * See {@link backendFetch} for URL, header, retry, and error behaviour.
 */
export async function backendRequest<T>(
  path: string,
  errorContext: string,
  init?: RequestInit,
): Promise<T> {
  const response = await backendFetch(path, errorContext, init);
  return response.json() as Promise<T>;
}

/**
 * Authenticated backend request for endpoints with no response body (e.g.
 * DELETE). Resolves on success; throws on a non-OK response.
 */
export async function backendRequestVoid(
  path: string,
  errorContext: string,
  init?: RequestInit,
): Promise<void> {
  await backendFetch(path, errorContext, init);
}
